import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
// @ts-ignore - JS components
import LoginModal from '../auth/LoginModal.js';
import type { PrintifyProduct, PrintifyVariant } from '../../types/printify';

interface VariantOption {
  size?: string;
  color?: string;
}

const pageContainerClass = 'min-h-screen pt-24 md:pt-28 pb-12 bg-[var(--background)] transition-colors duration-300';
const sectionSurfaceClass = 'bg-[var(--surface)] text-[var(--text-primary)] border border-border rounded-islamic shadow-md';
const subtleTextClass = 'text-[var(--text-secondary)]';

const PrintifyProductDetail: React.FC = () => {
  const { shopId, productId } = useParams<{ shopId: string; productId: string }>();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [product, setProduct] = useState<PrintifyProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<PrintifyVariant | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  const fetchProduct = useCallback(async () => {
    if (!shopId || !productId || !user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response: PrintifyProduct = await apiService.get(`/printify/shops/${shopId}/products/${productId}`);
      setProduct(response);

      if (response?.variants?.length) {
        const enabledVariant = response.variants.find(v => v.is_enabled);
        setSelectedVariant(enabledVariant || response.variants[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Unable to load product details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [shopId, productId, user]);

  useEffect(() => {
    if (user) {
      fetchProduct();
    }
  }, [fetchProduct, user]);

  // Extract unique sizes and colors from variants
  const availableSizes = useMemo(() => {
    if (!product?.variants) return [];
    const sizes = new Set<string>();
    product.variants.forEach((variant) => {
      if (variant.is_enabled && variant.title) {
        const sizeMatch = variant.title.match(/^(XS|S|M|L|XL|2XL|3XL|4XL|5XL)/);
        if (sizeMatch) sizes.add(sizeMatch[1]);
      }
    });
    return Array.from(sizes).sort((a, b) => {
      const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
      return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
    });
  }, [product]);

  const availableColors = useMemo(() => {
    if (!product?.variants) return [];
    const colors = new Set<string>();
    product.variants.forEach((variant) => {
      if (variant.is_enabled && variant.title) {
        const colorMatch = variant.title.match(/\/\s*(.+)$/);
        if (colorMatch) colors.add(colorMatch[1].trim());
      }
    });
    return Array.from(colors).sort();
  }, [product]);

  // Find matching variant based on selected size and color
  useEffect(() => {
    if (!product?.variants || !selectedSize || !selectedColor) return;
    
    const matchingVariant = product.variants.find(
      (v) => v.is_enabled && v.title && v.title.includes(selectedSize) && v.title.includes(selectedColor)
    );
    
    if (matchingVariant) {
      setSelectedVariant(matchingVariant);
    }
  }, [selectedSize, selectedColor, product]);

  // Set initial selections when product loads
  useEffect(() => {
    if (product?.variants && availableSizes.length > 0 && availableColors.length > 0) {
      if (!selectedSize) setSelectedSize(availableSizes[0]);
      if (!selectedColor) setSelectedColor(availableColors[0]);
    }
  }, [product, availableSizes, availableColors, selectedSize, selectedColor]);

  const showToastNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleAddToCart = async () => {
    if (!product || !selectedVariant || !user) {
      setShowLoginModal(true);
      return;
    }

    if (!selectedVariant.is_enabled) {
      showToastNotification('This variant is currently unavailable', 'error');
      return;
    }

    setIsAddingToCart(true);

    try {
      const mainImage = product.images?.find(img => img.is_default) || product.images?.[0];

      const cartItem = {
        _id: `${product.id}-${selectedVariant.id}`,
        title: product.title,
        price: selectedVariant.price / 100, // Convert cents to dollars
        imageUrl: mainImage?.src || null,
        quantity: quantity,
        variant: {
          id: selectedVariant.id,
          title: selectedVariant.title || 'Default',
          sku: selectedVariant.sku || ''
        },
        printifyProduct: {
          shopId: shopId!,
          productId: product.id,
          variantId: selectedVariant.id
        }
      };

      addToCart(cartItem);
      
      // Show success toast
      showToastNotification(`${quantity} ${quantity > 1 ? 'items' : 'item'} added to cart!`, 'success');
      
      // Reset quantity to 1 after adding
      setQuantity(1);
    } catch (err) {
      console.error('Error adding to cart:', err);
      showToastNotification('Failed to add to cart. Please try again.', 'error');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleImageChange = (direction: 'prev' | 'next') => {
    if (!product?.images) return;
    
    setSelectedImageIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % product.images!.length;
      } else {
        return (prev - 1 + product.images!.length) % product.images!.length;
      }
    });
  };

  if (!user) {
    return (
      <div className={pageContainerClass}>
        <LoginModal
          isOpen={showLoginModal}
          onClose={(success: any) => {
            if (!success) {
              navigate('/store');
            }
          }}
          onSwitchToRegister={() => {
            setShowLoginModal(false);
            navigate('/register', { state: { from: { pathname: `/store/product/${shopId}/${productId}` } } });
          }}
          returnPath={`/store/product/${shopId}/${productId}`}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={pageContainerClass}>
        <div className="container-custom min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full border-3 border-dashed border-[var(--primary)] animate-spin mb-4" />
            <p className={subtleTextClass}>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={pageContainerClass}>
        <div className="container-custom min-h-[60vh] flex items-center justify-center">
          <div className={`${sectionSurfaceClass} max-w-md p-8 text-center`}>
            <h3 className="text-xl font-semibold text-red-600 mb-2">Error</h3>
            <p className={subtleTextClass}>{error || 'Product not found'}</p>
            <button onClick={() => navigate('/store')} className="btn-primary mt-4">
              Back to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  const images = product.images || [];
  const currentImage = images[selectedImageIndex];
  const hasMultipleImages = images.length > 1;

  return (
    <div className={pageContainerClass}>
      <div className="container-custom space-y-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm">
          <button onClick={() => navigate('/store')} className="text-[var(--accent)] hover:underline">
            Store
          </button>
          <span className={subtleTextClass}>/</span>
          <span className={subtleTextClass}>{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className={`${sectionSurfaceClass} relative overflow-hidden`}>
              <div className="aspect-square">
                <img
                  src={currentImage?.src || 'https://via.placeholder.com/600x600?text=No+Image'}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Navigation Arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={() => handleImageChange('prev')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all"
                    aria-label="Previous image"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleImageChange('next')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all"
                    aria-label="Next image"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Image indicators */}
              {hasMultipleImages && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === selectedImageIndex ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                      aria-label={`View image ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {hasMultipleImages && (
              <div className="grid grid-cols-4 gap-2">
                {images.slice(0, 4).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`${sectionSurfaceClass} aspect-square overflow-hidden ${
                      idx === selectedImageIndex ? 'ring-2 ring-[var(--accent)]' : ''
                    }`}
                  >
                    <img src={img.src} alt={`${product.title} view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-3">{product.title}</h1>
              {product.description && (
                <div className="space-y-2">
                  <div className={`${subtleTextClass} text-lg leading-relaxed`}>
                    {isDescriptionExpanded
                      ? product.description.replace(/<[^>]*>/g, '')
                      : product.description.replace(/<[^>]*>/g, '').substring(0, 200) + 
                        (product.description.length > 200 ? '...' : '')}
                  </div>
                  {product.description.length > 200 && (
                    <button
                      onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                      className="text-[var(--accent)] hover:underline text-sm font-medium"
                    >
                      {isDescriptionExpanded ? 'Read less' : 'Read more'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Price */}
            {selectedVariant && (
              <div className="space-y-2">
                <div className="text-4xl font-bold text-[var(--accent)]">
                  ${(selectedVariant.price / 100).toFixed(2)}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className={`h-2 w-2 rounded-full ${selectedVariant.is_enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={subtleTextClass}>
                    {selectedVariant.is_enabled ? 'In Stock' : 'Out of Stock'}
                  </span>
                  <span className={`${subtleTextClass} ml-2`}>
                    • {availableSizes.length * availableColors.length} options available
                  </span>
                </div>
              </div>
            )}

            {/* Size Selection */}
            {availableSizes.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Select Size
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-islamic border-2 font-medium transition-all ${
                        selectedSize === size
                          ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                          : 'border-border bg-[var(--surface)] text-[var(--text-primary)] hover:border-[var(--accent)]'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {availableColors.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-[var(--text-primary)]">
                  Select Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-islamic border-2 font-medium transition-all ${
                        selectedColor === color
                          ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                          : 'border-border bg-[var(--surface)] text-[var(--text-primary)] hover:border-[var(--accent)]'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Quantity
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-islamic border border-border bg-[var(--surface)] hover:bg-[var(--surface-elevated)] transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 px-3 py-2 text-center rounded-islamic border border-border bg-[var(--surface)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-islamic border border-border bg-[var(--surface)] hover:bg-[var(--surface-elevated)] transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={!selectedVariant || !selectedVariant.is_enabled || isAddingToCart}
              className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAddingToCart ? 'Adding to Cart...' : 'Add to Cart'}
            </button>

            {/* Product Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="pt-4 border-t border-border">
                <p className="text-sm font-medium text-[var(--text-primary)] mb-2">Tags:</p>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-sm rounded-full bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/20 font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toast Notification */}
        {showToast && (
          <div className="fixed bottom-8 right-8 z-50 animate-slide-up">
            <div
              className={`${sectionSurfaceClass} px-6 py-4 rounded-islamic shadow-lg flex items-center gap-3 min-w-[300px] ${
                toastType === 'success' ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
              }`}
            >
              {toastType === 'success' ? (
                <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <div>
                <p className="font-medium text-[var(--text-primary)]">{toastMessage}</p>
              </div>
              <button
                onClick={() => setShowToast(false)}
                className="ml-auto text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintifyProductDetail;
